import { matchType } from "../base/base-type";
import { UserConst } from "./user";

export type UserBatchImportResult = {
  validateResult: UserBatchValidateResult;
};

export type UserBatchUpdateResult = UserBatchImportResult & {
  count: number;
};

export type UserBatchValidateResult = {
  isValid: boolean;
  results: UserBatchValidateResultItem[];
};

type UserBatchValidateResultItem = {
  user: {
    accountName: string;
    name: string;
  };
  errors: UserBatchImportError[];
};

export type UserBatchImportError = {
  errIdx: number;
  severity: UserBatchImportErrorSeveritiesLiterals;
  errCode: UserBatchImportErrorCodesLiterals;
  additionalErrData: string;
};

const UserBatchImportErrorSeverities = ["error", "warn"] as const;
type UserBatchImportErrorSeveritiesLiterals =
  (typeof UserBatchImportErrorSeverities)[number];

const UserBatchImportErrorCodes = [
  "account_name.invalid_format",
  "account_name.duplicated_in_csv",
  "account_name.already_exists",
  "account_name.not_exists",
  "account_name.cannot_specify_admin",
  "name.too_large_length",
  "name.empty",
  "name.duplicated_in_csv",
  "name.already_exists",
  "email.invalid_format",
  "role.cannot_specify_admin",
  "role.inconsistent_group",
  "role.admin_cannot_specify_with_other_role",
  "role.invalid_format",
  "group_id.not_exists",
  "group_role.invalid_format",
  "group_role.exceed_configurable_max_count",
  "password.invalid_format",
  "password.specified_for_not_password_auth_user",
  "disabled.invalid_format",
  "disabled.multi_group",
  "auth_method.invalid_format",
  "auth_method.org_saml_setting_not_configured",
  "external_user_id.already_exists",
  "external_user_id.duplicated_in_csv",
  "external_user_id.empty",
  "external_user_id.specified_for_not_saml_auth_user",
  "external_user_id.invalid_format",
  "invitation.email_empty",
  "invitation.duplicated_email_in_csv",
  "invitation.duplicated_email_with_existing_ones",
] as const;
type UserBatchImportErrorCodesLiterals =
  (typeof UserBatchImportErrorCodes)[number];

export const toErrorMessage = (
  code: UserBatchImportErrorCodesLiterals,
  additionalErrData?: string,
) => {
  return matchType<UserBatchImportErrorCodesLiterals, string>(code, {
    "account_name.invalid_format":
      "アカウント名は半角英数字または記号256字以内で入力してください。",
    "account_name.duplicated_in_csv": "CSV内に同じアカウント名が存在します。",
    "account_name.already_exists": "アカウント名が既に存在します。",
    "account_name.not_exists": "アカウント名が存在しません。",
    "account_name.cannot_specify_admin":
      "特権管理者のアカウントは更新できません。",
    "name.too_large_length": "氏名は64字以内で入力してください。",
    "name.empty": "氏名を入力してください。",
    "name.duplicated_in_csv": "CSV内に同じ氏名が存在します。確認してください",
    "name.already_exists": "氏名が既に存在します。確認してください。",
    "email.invalid_format": "メールアドレスを正しい形式で入力してください。",
    "role.cannot_specify_admin":
      "特権管理者（'Admin'）はロールに指定できません。'Staff','AccountManager'のいずれかを入力してください。",
    "role.inconsistent_group":
      "ロールとグループの整合性が取れていません。一般ユーザーの場合はグループを指定し、それ以外の場合はグループを指定しないでください。",
    "role.admin_cannot_specify_with_other_role":
      "特権管理者は他のロールと同時に指定できません。",
    "role.invalid_format":
      "ロールの表記が間違っています。'Staff','AccountManager'のいずれかを入力してください。",
    "group_id.not_exists": "グループIDが存在しません。",
    "group_role.invalid_format":
      "グループロールの表記が間違っています。グループ管理者の場合は'GroupManager'、スタッフの場合は'Staff'と入力してください。",
    "group_role.exceed_configurable_max_count": `ユーザーが所属可能なグループは${UserConst.MAX_GROUPS_LENGTH}個までです。`,
    "password.invalid_format":
      "パスワードを半角英数字記号8文字以上50文字以内で入力してください。",
    "password.specified_for_not_password_auth_user":
      "認証方法がパスワード認証ではない場合、パスワードは空文字を指定してください。",
    "disabled.invalid_format":
      "無効ユーザーの表記が間違っています。有効の場合は'false'、無効の場合は'true'と入力してください。",
    "disabled.multi_group": "複数のグループは指定できません。",
    "auth_method.invalid_format":
      "認証方法の表記が間違っています。パスワード認証の場合は'password'、SAML認証の場合は'saml'と入力してください。",
    "auth_method.org_saml_setting_not_configured":
      "SAML設定が存在しないため、SAML認証を指定できません。SAML認証を使う場合は、SAML設定を作成してください。",
    "external_user_id.already_exists":
      "指定された外部ユーザーIDを持つユーザーは既に存在します。",
    "external_user_id.duplicated_in_csv":
      "CSV内に同じ外部ユーザーIDが存在します。重複を削除してください。",
    "external_user_id.empty": "外部ユーザーIDを入力してください。",
    "external_user_id.specified_for_not_saml_auth_user":
      "認証方法がSAML認証ではない場合、外部ユーザーIDは空文字を指定してください。",
    "external_user_id.invalid_format":
      "外部ユーザーIDは半角英数字または記号256字以内で入力してください。",
    "invitation.email_empty":
      "メールアドレスが未入力のため、仮パスワードを送信できません",
    "invitation.duplicated_email_in_csv": `CSV内の${additionalErrData}とメールアドレスが重複しているため、仮パスワードを送信できません。`,
    "invitation.duplicated_email_with_existing_ones": `既存の${additionalErrData}とメールアドレスが重複しているため、仮パスワードを送信できません。`,
  });
};
